/**
 * Offline-queue helpers used by every practice app to store save requests
 * that were made while the browser was offline, then flush them once we
 * reconnect.
 *
 * Each app gets its own set of localStorage keys via `createPendingStore`
 * (e.g. "qb_pendingGames", "wr_pendingGames") so one app's queue never
 * clobbers another's on the same origin.
 *
 * Migrations: earlier builds used unprefixed keys ("pendingGames"). Reads
 * transparently fall back to the legacy key and delete it once migrated.
 */

import type { AxiosRequestConfig } from "axios";
import libraryServices from "@workspace/shared/core/services/library/library.services";
import { gameDateForDay } from "@workspace/shared/core/utils/practiceDate";
import type {
  LibraryGame,
  SavePlaysPayload,
  SaveSettingsPayload,
} from "@workspace/shared/core/services/library/library.types";

type PendingKeys = {
  settings: string;
  games: string;
  plays: string;
  deletes: string;
  /** qb/st offline play-cell edits queued by the transformedPlays slice. */
  updatedPlays: string;
  /** Saved games still to be moved to the day their coach confirmed. */
  days: string;
};

/** A saved game still to be moved to the day its coach confirmed in Save Game (client 2026-11-04). */
export type PendingDayMove = { game_id: number; day: string };

// 400 = payload the backend will never accept; 404 = the target game no
// longer exists (deleted from another device). Neither can succeed by
// retrying, and a stuck entry here blocked sign-out from EVERY app until
// the coach chose "Log out anyway" — which wipes local data.
const isPermanentRejection = (err: unknown): boolean => {
  const status = (err as { response?: { status?: number } })?.response?.status;
  return status === 400 || status === 404;
};

/** A day move that can be sent: anything else in the queue (a damaged entry) is dropped. */
const isValidDayMove = (move: unknown): move is PendingDayMove => {
  const m = move as Partial<PendingDayMove> | null;
  return typeof m?.game_id === "number" && typeof m.day === "string" && gameDateForDay(m.day) != null;
};

type LegacyKeys = {
  settings: string;
  games: string;
  plays: string;
};

const LEGACY_KEYS: LegacyKeys = {
  settings: "pendingSettings",
  games: "pendingGames",
  plays: "pendingPlays",
};

function safeRead<T>(key: string): T[] {
  const raw = localStorage.getItem(key);
  if (raw === null) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}

/** Read prefixed key, falling back to legacy unprefixed key (and migrating). */
function readPending<T>(key: string, legacyKey: string): T[] {
  const prefixed = localStorage.getItem(key);
  if (prefixed !== null) return safeRead<T>(key);

  const legacy = localStorage.getItem(legacyKey);
  if (legacy === null) return [];
  try {
    const parsed = JSON.parse(legacy) as T[];
    localStorage.setItem(key, legacy);
    localStorage.removeItem(legacyKey);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export type PendingStore = {
  keys: PendingKeys;
  pushSettings: (settings: SaveSettingsPayload) => void;
  pushGame: (game: LibraryGame) => void;
  pushPlays: (payload: SavePlaysPayload) => void;
  /** Queue a play deletion that couldn't reach the backend (dedupes). */
  pushDelete: (playId: string | number) => void;
  readSettings: () => SaveSettingsPayload[];
  readGames: () => LibraryGame[];
  readPlays: () => SavePlaysPayload[];
  readDeletes: () => string[];
  readDays: () => PendingDayMove[];
  clearSettings: () => void;
  clearGames: () => void;
  clearPlays: () => void;
  clearDeletes: () => void;
  /**
   * Push all queued saves to the backend. Order is deterministic:
   * settings → games → plays → deletes. Returns true if ANY items were successfully
   * flushed. Callers use this signal to trigger a games-list refresh so
   * offline-queued rows are reconciled with their server-assigned IDs
   * (otherwise the offline temp row + the new server row appear as a
   * duplicate until the user reloads).
   *
   * `config` is forwarded to every underlying request — the cross-app sweep
   * passes an explicit `x-app-selected` header so a drain routes to the right
   * app regardless of the shared global app key.
   */
  syncPending: (config?: AxiosRequestConfig) => Promise<boolean>;
  /**
   * After a save: move the game to the day its coach confirmed, unless it
   * landed on it (`savedAt`: the date the API gave it). Never throws — a move
   * that can't go now is queued and sent with the next sync; one the API
   * will never take (the game gone) is dropped.
   */
  keepConfirmedDay: (
    gameId: number,
    day: string | null | undefined,
    savedAt?: string | null,
    config?: AxiosRequestConfig,
  ) => Promise<void>;
  /** The game's date was set another way (the Library): a queued move for it would undo that. */
  cancelDayMove: (gameId: number) => void;
};

/**
 * Build a per-app pending store. `appKey` becomes the localStorage prefix
 * (e.g. "qb" → "qb_pendingSettings"). The store methods automatically hit
 * shared libraryServices so all apps use the same endpoints.
 */
export function createPendingStore(appKey: string): PendingStore {
  const keys: PendingKeys = {
    settings: `${appKey}_pendingSettings`,
    games: `${appKey}_pendingGames`,
    plays: `${appKey}_pendingPlays`,
    deletes: `${appKey}_pendingDeletes`,
    // Written by the transformedPlays slice (pendingLocalStorageKey) — the
    // name must stay in lockstep with createTransformedPlaysSlice's option.
    updatedPlays: `${appKey}_pendingUpdatedPlays`,
    days: `${appKey}_pendingDays`,
  };

  const push = <T>(key: string, value: T) => {
    const current = safeRead<T>(key);
    current.push(value);
    localStorage.setItem(key, JSON.stringify(current));
  };

  const readSettings = () =>
    readPending<SaveSettingsPayload>(keys.settings, LEGACY_KEYS.settings);
  const readGames = () => readPending<LibraryGame>(keys.games, LEGACY_KEYS.games);
  const readPlays = () =>
    readPending<SavePlaysPayload>(keys.plays, LEGACY_KEYS.plays);
  // No legacy fallback — the deletes queue is new with this build.
  const readDeletes = () => safeRead<string>(keys.deletes);
  // Nor for day moves (client 2026-11-04).
  const readDays = () => safeRead<unknown>(keys.days).filter(isValidDayMove);

  const clearSettings = () => localStorage.removeItem(keys.settings);
  const clearGames = () => localStorage.removeItem(keys.games);
  const clearPlays = () => localStorage.removeItem(keys.plays);
  const clearDeletes = () => localStorage.removeItem(keys.deletes);

  const pushDelete = (playId: string | number) => {
    const id = String(playId);
    const current = readDeletes();
    if (current.includes(id)) return;
    current.push(id);
    localStorage.setItem(keys.deletes, JSON.stringify(current));
  };

  const syncPending = async (
    config?: AxiosRequestConfig,
  ): Promise<boolean> => {
    const pendingSettings = readSettings();
    const pendingGames = readGames();
    const pendingPlays = readPlays();
    const pendingDays = safeRead<unknown>(keys.days);
    let anyFlushed = false;

    if (pendingSettings.length > 0) {
      for (const s of pendingSettings) {
        try {
          await libraryServices.onSaveSettings(s, config);
        } catch (err) {
          console.error(`[${appKey}] Failed to sync settings:`, err);
          return anyFlushed;
        }
      }
      clearSettings();
      anyFlushed = true;
    }

    if (pendingGames.length > 0) {
      for (const queued of pendingGames) {
        // The day its coach confirmed is set once the game exists; POST
        // /games never sees it.
        const { confirmed_day: confirmedDay, ...g } = queued;
        try {
          const resp = await libraryServices.onSaveGame(g, config);
          // Offline-queued games carry their plays embedded, but the
          // backend's POST /games IGNORES inline plays — without this
          // follow-up POST the plays would be silently dropped and the
          // queue cleared, i.e. permanent data loss. POST /games returns
          // the raw entity (camelCase gameId); handle both shapes.
          const raw = resp?.data as
            | { game_id?: number; gameId?: number; game_date?: string; gameDate?: string }
            | undefined;
          const gameId = raw?.game_id ?? raw?.gameId;
          if (Array.isArray(g.plays) && g.plays.length > 0) {
            if (gameId == null) {
              // The game POST succeeded but returned no resolvable id, so we
              // cannot attach its plays. DON'T fall through to clearGames()
              // and silently drop them — bail with the queue intact so a
              // later retry re-runs this game. (Preserving the coach's plays
              // outweighs a possible duplicate game row on the rare retry.)
              console.error(
                `[${appKey}] Saved game returned no game_id; keeping queue to avoid losing ${g.plays.length} play(s)`,
              );
              return anyFlushed;
            }
            await libraryServices.onSavePlays(
              {
                game_id: gameId,
                plays: g.plays,
              },
              config,
            );
          }
          // The game and its plays are saved: a move that fails now is
          // queued on its own, never a reason to send them again.
          if (gameId != null && confirmedDay) {
            await keepConfirmedDay(gameId, confirmedDay, raw?.gameDate ?? raw?.game_date, config);
            // Handled (moved, or queued on its own): should a later game keep
            // this one in the queue, sending it again mustn't move it again —
            // its date may have been changed in the Library since.
            forgetConfirmedDay(queued);
          }
        } catch (err) {
          console.error(`[${appKey}] Failed to sync game:`, err);
          return anyFlushed;
        }
      }
      clearGames();
      anyFlushed = true;
    }

    // Day moves, once their games exist — those queued before this sync
    // began (read with the rest above): one queued by a game just above
    // waits for the next sync rather than going twice. One that can't go
    // yet waits too, without holding up the plays below.
    if (pendingDays.length > 0) {
      const done: unknown[] = [];
      for (const move of pendingDays) {
        // Cancelled since this sync began (a date set in the Library): skip it.
        const stillQueued = safeRead<unknown>(keys.days).some((m) => JSON.stringify(m) === JSON.stringify(move));
        if (isValidDayMove(move) && stillQueued) {
          try {
            await libraryServices.onMoveGameToDay(move.game_id, move.day, config);
          } catch (err) {
            if (!isPermanentRejection(err)) {
              console.warn(`[${appKey}] Game ${move.game_id} not moved to ${move.day} yet:`, err);
              break;
            }
            console.warn(`[${appKey}] Game ${move.game_id} can't be moved to ${move.day}; dropped:`, err);
          }
        }
        done.push(move);
        anyFlushed = true;
      }
      // Only what went (or was dropped) leaves: a move queued meanwhile — by
      // a save, or another sync — stays.
      const sent = done.map((move) => JSON.stringify(move));
      removeDayMoves((move) => {
        const at = sent.indexOf(JSON.stringify(move));
        if (at < 0) return false;
        sent.splice(at, 1);
        return true;
      });
    }

    // A payload the backend PERMANENTLY rejects (400 — e.g. a play that
    // belongs to another game) must not wedge the queue forever and block
    // everything behind it. Park it in a dead-letter key (data preserved
    // for recovery, never silently dropped) and keep flushing.
    const deadLetter = (kind: string, payload: unknown) => {
      try {
        const key = `${appKey}_deadLetter${kind}`;
        const current = safeRead<unknown>(key);
        current.push(payload);
        localStorage.setItem(key, JSON.stringify(current));
        console.error(
          `[${appKey}] Backend permanently rejected a ${kind} payload; parked at ${key}`,
        );
      } catch {
        /* quota — nothing more we can do */
      }
    };
    if (pendingPlays.length > 0) {
      const remainingPlays = [...pendingPlays];
      for (const p of pendingPlays) {
        try {
          await libraryServices.onSavePlays(p, config);
          remainingPlays.splice(remainingPlays.indexOf(p), 1);
          anyFlushed = true;
        } catch (err) {
          if (isPermanentRejection(err)) {
            remainingPlays.splice(remainingPlays.indexOf(p), 1);
            deadLetter("Plays", p);
            continue;
          }
          console.error(`[${appKey}] Failed to sync plays:`, err);
          localStorage.setItem(keys.plays, JSON.stringify(remainingPlays));
          return anyFlushed;
        }
      }
      clearPlays();
    }

    // Offline play-cell EDITS (qb/st playlist). Each entry is already an
    // onSavePlays payload { game_id, plays }. Drained here so the app's OWN
    // reconnect/interval flush pushes them — previously only the dashboard's
    // sweep did, so edits made offline sat locally until the coach happened
    // to visit the dashboard. Runs before deletes so a queued delete of the
    // same play still wins.
    const pendingUpdated = safeRead<{ game_id?: string | number; plays?: unknown[] }>(
      keys.updatedPlays,
    );
    if (pendingUpdated.length > 0) {
      const remainingUpdated = [...pendingUpdated];
      for (const group of pendingUpdated) {
        const plays = Array.isArray(group?.plays) ? group.plays : [];
        if (!group?.game_id || plays.length === 0) {
          remainingUpdated.splice(remainingUpdated.indexOf(group), 1);
          continue;
        }
        try {
          await libraryServices.onSavePlays(
            { game_id: group.game_id, plays } as SavePlaysPayload,
            config,
          );
          remainingUpdated.splice(remainingUpdated.indexOf(group), 1);
          anyFlushed = true;
        } catch (err) {
          if (isPermanentRejection(err)) {
            remainingUpdated.splice(remainingUpdated.indexOf(group), 1);
            deadLetter("UpdatedPlays", group);
            continue;
          }
          console.error(`[${appKey}] Failed to sync updated plays:`, err);
          localStorage.setItem(
            keys.updatedPlays,
            JSON.stringify(remainingUpdated),
          );
          return anyFlushed;
        }
      }
      localStorage.removeItem(keys.updatedPlays);
    }

    // Deletes flush LAST: if an upsert of the same play was queued above it
    // lands first, then the delete wins — matching the user's final intent.
    const pendingDeletes = readDeletes();
    if (pendingDeletes.length > 0) {
      const remaining = [...pendingDeletes];
      for (const playId of pendingDeletes) {
        try {
          await libraryServices.onDeletePlay(playId, config);
          remaining.splice(remaining.indexOf(playId), 1);
          anyFlushed = true;
        } catch (err) {
          const status = (err as { response?: { status?: number } })?.response
            ?.status;
          // 404 = the play never reached (or already left) the server;
          // 403 = not ours. Neither can succeed by retrying — drop the
          // entry so it can't wedge the queue forever.
          if (status === 404 || status === 403) {
            remaining.splice(remaining.indexOf(playId), 1);
            anyFlushed = true;
            continue;
          }
          console.error(`[${appKey}] Failed to sync play delete:`, err);
          localStorage.setItem(keys.deletes, JSON.stringify(remaining));
          return anyFlushed;
        }
      }
      clearDeletes();
    }

    return anyFlushed;
  };

  /** Rewrites the day-move queue without the moves `gone` matches (read and written at once). */
  const removeDayMoves = (gone: (move: unknown) => boolean) => {
    const left = safeRead<unknown>(keys.days).filter((move) => !gone(move));
    if (left.length > 0) localStorage.setItem(keys.days, JSON.stringify(left));
    else localStorage.removeItem(keys.days);
  };

  /** A queued game whose day move was handled: drop its confirmed day (by its idempotency key). */
  const forgetConfirmedDay = (game: LibraryGame) => {
    if (!game.idempotency_key) return;
    const queued = readGames();
    const entry = queued.find((g) => g.idempotency_key === game.idempotency_key);
    if (!entry || entry.confirmed_day === undefined) return;
    delete entry.confirmed_day;
    localStorage.setItem(keys.games, JSON.stringify(queued));
  };

  const keepConfirmedDay: PendingStore["keepConfirmedDay"] = async (gameId, day, savedAt, config) => {
    const move = libraryServices.confirmedDayToKeep(day, savedAt);
    if (!move) return;
    try {
      await libraryServices.onMoveGameToDay(gameId, move, config);
    } catch (err) {
      if (isPermanentRejection(err)) {
        console.warn(`[${appKey}] Game ${gameId} can't be moved to ${move}:`, err);
        return;
      }
      console.warn(`[${appKey}] Game ${gameId} saved; its move to ${move} is queued:`, err);
      try {
        // The newest day for a game is the one that counts.
        removeDayMoves((m) => isValidDayMove(m) && m.game_id === gameId);
        push<PendingDayMove>(keys.days, { game_id: gameId, day: move });
      } catch (storageErr) {
        // Storage full: the game is saved either way; its date can be set in the Library.
        console.warn(`[${appKey}] Game ${gameId}'s move to ${move} couldn't be queued:`, storageErr);
      }
    }
  };

  const cancelDayMove: PendingStore["cancelDayMove"] = (gameId) => {
    try {
      removeDayMoves((m) => isValidDayMove(m) && m.game_id === gameId);
    } catch {
      /* storage unavailable: nothing queued to cancel */
    }
  };

  return {
    keys,
    pushSettings: (s) => push(keys.settings, s),
    pushGame: (g) => push(keys.games, g),
    pushPlays: (p) => push(keys.plays, p),
    pushDelete,
    readSettings,
    readGames,
    readPlays,
    readDeletes,
    readDays,
    clearSettings,
    clearGames,
    clearPlays,
    clearDeletes,
    syncPending,
    keepConfirmedDay,
    cancelDayMove,
  };
}
